import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { actionCreators } from '@/store/OutputReportGroup'
import { Main, MainBody, MainTop, MainTopTitle, WhiteBox } from '@/components/Layout'
import Resx from '@/components/Resx'
import ErrorBoundary from '@/components/ErrorBoundary'
import { BsrsGrid, Column, commandColumnProps } from '@/components/BsrsGrid'
import context from '@/context'
import { format } from '@/utils/StringUtilities'
import ReportAttachments from '@/components/reporting/reportAttachments'
import LoadingIndicator from '@/components/LoadingIndicator'
import { formatDate } from '@/utils/dateUtilities'
import Name from '@/components/Name'
import { Row, Col } from 'reactstrap'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import getEnumTranslation from '@/enums/enums'
import { OutputReportGroupState } from '@/enums/outputReportGroupState'
import { useDispatch } from '@/hooks/dispatch'
import { useLocalization } from '@/hooks/localization'
import { UploadReports } from '@/components/reporting/reportUpload'
import OutputReportDownloadButtons from '@/internal-app/pages/outputReports/outputReportDownloadButtons'
import { Button } from '@/components/button'
import { dialogTypes } from '@/dialogs'
import { useDialog } from '@/hooks/useDialog'

const OutputReportsPage = React.memo(({ match, addAttachments }) => {
  const [get] = useDispatch([actionCreators.get])
  const { outputReportGroup, fetching, attachmentsKey, adding, reportUploadsKey } = useSelector(state => {
    const { outputReportGroup, fetching, attachmentsKey, adding, reportUploadsKey } = state.outputReportGroup
    return { outputReportGroup, fetching, attachmentsKey, adding, reportUploadsKey }
  })
  const { outputReportGroupState, creationTime } = outputReportGroup || {}
  const { institutions, outputReportSources } = outputReportGroup || {}
  const { localize } = useLocalization()

  const parentId = match.params.parentId
  const openDialog = useDialog()

  useEffect(() => {
    get(parentId)
  }, [get, parentId])

  const state = getEnumTranslation({
    localize,
    enumName: OutputReportGroupState.enumName,
    value: outputReportGroupState
  })

  const onRemoveReportUpload = useCallback(id => context.api.outputReportGroupService.removeReportUploads([id], parentId), [parentId])
  const reportUploadDataSource = useCallback(() => context.api.outputReportGroupService.getReportUploads(parentId), [parentId])
  const dataSource = useCallback(request => context.api.outputReportService.getTableData(parentId, localize('Common.CBI'), false, request),
    [localize, parentId])
  const attachmentsDataSource = useCallback(() => context.api.outputReportGroupService.getAttachments(parentId),
    [parentId])
  const onRemoveAttachment = useCallback(id => this.props.removeAttachments({ entityId: parentId, fileIds: [id] }), [parentId])

  const openReportInstitutions = useCallback(() => {
    openDialog({
      dialogType: dialogTypes.outputReportInstitutions,
      institutions
    })
  }, [openDialog, institutions])

  const openReportSources = useCallback(() => {
    openDialog({
      dialogType: dialogTypes.outputReportSources,
      outputReportSources
    })
  }, [openDialog, outputReportSources])

  if (fetching || !outputReportGroup) { return <LoadingIndicator /> }

  return <Main>
    <MainTop>
      <MainTopTitle backUrl='/output-report-groups'>
        <Resx name='Reporting.OutputReportGroup' />
      </MainTopTitle>
    </MainTop>
    <MainBody transparent>
      <React.Fragment>
        <ErrorBoundary>
          <WhiteBox header='Reporting.General'>
            <Row>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel><Resx name='Common.Name' /></InfoLabel>
                  <InfoContent><Name data={outputReportGroup} /></InfoContent>
                </InfoRow>
              </Col>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel><Resx name='Common.CreationTime' /></InfoLabel>
                  <InfoContent>{formatDate(creationTime)}</InfoContent>
                </InfoRow>
              </Col>
            </Row>
            <Row>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel><Resx name='Reporting.OutputReportGroup' /></InfoLabel>
                  <InfoContent>{outputReportGroup.outputReportGroupDefinition.name}</InfoContent>
                </InfoRow>
              </Col>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel>State</InfoLabel>
                  <InfoContent>{state}</InfoContent>
                </InfoRow>
              </Col>
            </Row>
            <Row>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel><Resx name='Institutions.Institution' /></InfoLabel>
                  { institutions && institutions.length && <InfoContent>
                    {
                      institutions.length > 1 ? <Button size='sm' onClick={openReportInstitutions}><span>{ localize('Common.Detail')} ({institutions.length})</span></Button>
                        : <div>{institutions[0].name}</div>
                    }
                  </InfoContent> }
                </InfoRow>
              </Col>
              <Col sm='6'>
                <InfoRow>
                  <InfoLabel><Resx name='Reporting.SourceReports' /></InfoLabel>
                  { outputReportSources && outputReportSources.length > 0 && <InfoContent>
                    {
                      outputReportSources.length > 1 ? <Button size='sm' onClick={openReportSources}><span>{ localize('Common.Detail')} ({outputReportSources.length})</span></Button>
                        : <div>{outputReportSources[0].name}</div>
                    }
                  </InfoContent> }
                </InfoRow>
              </Col>
            </Row>
          </WhiteBox>
        </ErrorBoundary>
        <WhiteBox
          header='Reporting.Sheets'
          buttons={<ErrorBoundary>
            <OutputReportDownloadButtons outputReportGroup={outputReportGroup} />
          </ErrorBoundary>}
        >
          <BsrsGrid defaultSort={[{ field: 'order', dir: 'asc' }]} dataSource={dataSource}>
            <Column field='order' title={localize('Common.Order')} filter='numeric' editable={false} />
            <Column field='name' title={localize('Common.Name')} editable={false} />
            <Column field='latinName' title={localize('Common.LatinName')} editable={false} />
            <Column field='datasetNumber' title={localize('Common.DatasetNumber')} filter='numeric' editable={false} />

            <Column {...commandColumnProps({ detailUrl: x => format('/output-report-groups/{0}/output-reports/{1}', parentId, x.id) })} title=' ' width={150} />
          </BsrsGrid>
        </WhiteBox>

        {outputReportGroupState === OutputReportGroupState.enum.Editable && <UploadReports
          key={reportUploadsKey}
          dataSource={reportUploadDataSource}
          downloadAction={context.api.outputReportGroupService.downloadReportUpload}
          parentId={parentId}
          addReportUploads={context.api.outputReportGroupService.addReportUploads}
          removeReportUploads={onRemoveReportUpload}
          applyReportUpload={context.api.outputReportGroupService.importDataFromFile} />}

        <ErrorBoundary>
          <ReportAttachments
            disabled
            key={attachmentsKey}
            dataSource={attachmentsDataSource}
            downloadAction={context.api.outputReportGroupService.download}
            parentId={parentId}
            addAttachments={addAttachments}
            removeAttachment={onRemoveAttachment}
            adding={adding} />
        </ErrorBoundary>
      </React.Fragment>
    </MainBody>
  </Main>
})

export { OutputReportsPage }



// WEBPACK FOOTER //
// ./src/internal-app/pages/outputReports/outputReports.js